import { addNegativeToast } from 'modules/toastContainer';
import { FieldErrors, FieldValues } from 'react-hook-form';
import {
  isAlphaNumericDashesPeriod,
  isAlphaNumericPeriodUnderscores,
  noSpaces,
} from 'utils/validation';
import { createIntegration, updateIntegration } from '../actions';
import {
  ADDING_APNS_ERROR,
  ADDING_FIREBASE_ERROR,
  ASSIGNED_ANDROID_INTEGRATION_OPTIONS,
  ASSIGNED_IOS_INTEGRATION_OPTIONS,
  ASSIGNED_IOS_INTEGRATION_OPTIONS_WITHOUT_FIREBASE,
  INTEGRATION_FORM_CONTENT,
} from '../constants';
import {
  AppPlatform,
  Integration,
  IntegrationAuthType,
  IntegrationOption,
  IntegrationPlatform,
  IntegrationType,
} from '../types';

/**
 * getStoreURLPlaceholder
 * returns placeholder text to set for the store url option
 * "Web" has no store url field.
 */
export const getStoreURLPlaceholder = (platform?: AppPlatform) =>
  platform === AppPlatform.IOS
    ? 'http://appstore.com/myapp'
    : 'https://play.google.com/store/apps/details?id=com.myapp.app';

/**
 * getPackageLabel
 * returns name of label to set for the platform option
 */
export const getPackageLabel = (platform?: AppPlatform) => {
  if (platform === AppPlatform.ANDROID || platform === AppPlatform.OTT) return 'Package name';
  if (platform === AppPlatform.IOS) return 'Bundle ID';
  return 'Website identifier';
};

/**
 * setIcon
 * returns name of React-Semantic item to use.
 */
export const setIcon = (platform: string) =>
  platform === AppPlatform.ANDROID ? 'android' : 'apple';

/**
 * checks whether an input is considered valide based on provided params
 */
export const isInvalidInput = (
  name: string,
  packageName: string,
  platform: AppPlatform | undefined,
  hasError = false,
) => !name || !packageName || !platform || hasError;

/**
 * validates the package name during mobile app creation.
 * all three platform types have separate validation rules
 */
export const validatePackageName = (platform: AppPlatform | undefined, packageName: string) => {
  if (platform === AppPlatform.IOS) return isAlphaNumericDashesPeriod(packageName);
  if (platform === AppPlatform.ANDROID) return isAlphaNumericPeriodUnderscores(packageName);
  return noSpaces(packageName);
};

/** Get the form content for a particular IntegrationAuthType */
export const getSelectedFormContent = (authType?: IntegrationAuthType) =>
  Object.values(INTEGRATION_FORM_CONTENT.authMethods).find((content) => content.id === authType);

/**
 * @param assignedIntegrations List of integrations already assigned to the app
 * @returns `assignedGcmIntegration`, `assignedApnsIntegration`, `assignedApnsSandboxIntegration`:
 *  Integration assigned per integration platform type, `undefined` if nothing assigned to that platform
 */
export const getAssignedIntegrations = (assignedIntegrations?: IntegrationType) => {
  const assignedGcmIntegration = assignedIntegrations?.GCM?.[0];
  const assignedApnsIntegration = assignedIntegrations?.APNS?.[0];
  const assignedApnsSandboxIntegration = assignedIntegrations?.APNS_SANDBOX?.[0];

  return { assignedGcmIntegration, assignedApnsIntegration, assignedApnsSandboxIntegration };
};

/**
 * Handles the logic around creating and updating integrations.
 *
 * @param integration The integration that is added or replaces the existing integration for a given environment
 * @param assignedIntegrations List of integrations already assigned to the app
 */
export const determineIntegrationRequest = (
  integration: Integration,
  assignedIntegrations?: IntegrationType,
) => {
  if (!assignedIntegrations) return createIntegration(integration);

  const { assignedGcmIntegration, assignedApnsIntegration, assignedApnsSandboxIntegration } =
    getAssignedIntegrations(assignedIntegrations);

  if (integration.authType === IntegrationAuthType.FIREBASE_JSON) {
    /** We do not allow GCM and APNS/APNS_SANDBOX integrations to coexist on the same iOS app */
    if (assignedApnsIntegration || assignedApnsSandboxIntegration)
      return addNegativeToast(ADDING_FIREBASE_ERROR);

    return (assignedGcmIntegration ? updateIntegration : createIntegration)(integration);
  }

  /** We do not allow GCM and APNS/APNS_SANDBOX integrations to coexist on the same iOS app */
  if (assignedGcmIntegration) return addNegativeToast(ADDING_APNS_ERROR);

  const assignedIntegrationForPlatform = assignedIntegrations[integration.platform]?.[0];
  return assignedIntegrationForPlatform
    ? updateIntegration({ ...integration, name: assignedIntegrationForPlatform.name })
    : createIntegration(integration);
};

/**
 * Handles the logic around creating and updating integrations, specifically taking into account
 * additional logic for create flow for p8 integrations.
 *
 * Whenever the user creates a new p8 integration, since the credentials for both prod and sandbox are
 * the same, we can automatically create both prod and sandbox integrations on the same mobile app.
 *
 * @param integration The integration that is added or replaces the existing integration for a given environment
 * @param assignedIntegrations List of integrations already assigned to the app
 */
export const determineCreateIntegrationRequests = (
  integration: Integration,
  assignedIntegrations?: IntegrationType,
) => {
  /** Specifically when going through the CREATE flow for p8 integrations, we create both prod and sandbox */
  if (integration.authType === IntegrationAuthType.P8) {
    const { assignedGcmIntegration, assignedApnsIntegration, assignedApnsSandboxIntegration } =
      getAssignedIntegrations(assignedIntegrations);

    /** We do not allow GCM and APNS/APNS_SANDBOX integrations to coexist on the same iOS app */
    if (assignedGcmIntegration) return [addNegativeToast(ADDING_APNS_ERROR)];

    return [
      assignedApnsIntegration
        ? updateIntegration({ ...integration, name: assignedApnsIntegration.name })
        : createIntegration(integration),
      assignedApnsSandboxIntegration
        ? updateIntegration({
            ...integration,
            platform: IntegrationPlatform.APNS_SANDBOX,
            name: assignedApnsSandboxIntegration.name,
          })
        : createIntegration({ ...integration, platform: IntegrationPlatform.APNS_SANDBOX }),
    ];
  }

  return [determineIntegrationRequest(integration, assignedIntegrations)];
};

/** Returns error message strings keyed by field name. */
export const getFormErrorMessages = (errors: FieldErrors<FieldValues>) =>
  Object.entries(errors).reduce((acc: Record<string, string>, [fieldName, error]) => {
    acc[fieldName] = error?.message?.toString() || '';
    return acc;
  }, {});

/** Returns integration options based on app platform and integration platform */
export const getIntegrationOptions = (
  appPlatform: AppPlatform | undefined,
  integrations: IntegrationType,
): IntegrationOption[] => {
  if (!appPlatform) return [{} as IntegrationOption];

  if (appPlatform === AppPlatform.ANDROID) return ASSIGNED_ANDROID_INTEGRATION_OPTIONS;

  return integrations[IntegrationPlatform.GCM]
    ? ASSIGNED_IOS_INTEGRATION_OPTIONS
    : ASSIGNED_IOS_INTEGRATION_OPTIONS_WITHOUT_FIREBASE;
};

export const getEnvironmentLabelByPlatform = (platform: IntegrationPlatform | undefined) => {
  switch (platform) {
    case IntegrationPlatform.APNS:
      return 'Production';
    case IntegrationPlatform.APNS_SANDBOX:
      return 'Sandbox';
    case IntegrationPlatform.GCM:
      return 'Firebase';
    default:
      return '';
  }
};
